import React, { useEffect, useRef } from 'react';
import { Award, Users, Clock, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './About.css';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const About = () => {
    const { t } = useTranslation();
    const sectionRef = useRef(null);
    const contentRef = useRef(null);
    const imageRef = useRef(null);
    const featuresRef = useRef(null);

    useEffect(() => {
        // Animate content
        if (contentRef.current) {
            gsap.fromTo(
                contentRef.current.children,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: contentRef.current,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        // Animate image
        if (imageRef.current) {
            gsap.fromTo(
                imageRef.current,
                { opacity: 0, scale: 0.9, x: -50 },
                {
                    opacity: 1,
                    scale: 1,
                    x: 0,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: imageRef.current,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        // Animate features
        if (featuresRef.current) {
            gsap.fromTo(
                featuresRef.current.children,
                { opacity: 0, x: -30 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.6,
                    ease: 'power3.out',
                    stagger: 0.15,
                    scrollTrigger: {
                        trigger: featuresRef.current,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => {
                if (sectionRef.current && trigger.vars.trigger === sectionRef.current) {
                    trigger.kill();
                }
            });
        };
    }, []);

    return (
        <section id="about" ref={sectionRef} className="about">
            <div className="about-container">
                <div ref={contentRef} className="about-content">
                    <div className="section-badge">{t('about.badge')}</div>
                    <h2 className="section-title">
                        {t('about.title')}
                        <span className="gradient-text"> {t('about.titleHighlight')}</span>
                    </h2>
                    <p className="about-description">
                        {t('about.description1')}
                    </p>
                    <p className="about-description">
                        {t('about.description2')}
                    </p>
                    <div ref={featuresRef} className="about-features">
                        <div className="about-feature">
                            <Award size={24} className="feature-icon" />
                            <div className="feature-content">
                                <h3>{t('about.features.award.title')}</h3>
                                <p>{t('about.features.award.description')}</p>
                            </div>
                        </div>
                        <div className="about-feature">
                            <Users size={24} className="feature-icon" />
                            <div className="feature-content">
                                <h3>{t('about.features.team.title')}</h3>
                                <p>{t('about.features.team.description')}</p>
                            </div>
                        </div>
                        <div className="about-feature">
                            <Clock size={24} className="feature-icon" />
                            <div className="feature-content">
                                <h3>{t('about.features.care.title')}</h3>
                                <p>{t('about.features.care.description')}</p>
                            </div>
                        </div>
                        <div className="about-feature">
                            <Shield size={24} className="feature-icon" />
                            <div className="feature-content">
                                <h3>{t('about.features.trusted.title')}</h3>
                                <p>{t('about.features.trusted.description')}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div ref={imageRef} className="about-image">
                    <div className="image-wrapper">
                        <div className="image-placeholder-large">
                            <img 
                                src={`${process.env.PUBLIC_URL || ''}/about.jpg`} 
                                alt="Patient receiving quality dental care at Ishita Dental Clinic" 
                                className="about-patient-image"
                            />
                        </div>
                        <div className="floating-stats stats-1">
                            <div className="stat-icon">🏆</div>
                            <div className="stat-content">
                                <span className="stat-value">2</span>
                                <span className="stat-label">Gold Medals</span>
                            </div>
                        </div>
                        <div className="floating-stats stats-2">
                            <div className="stat-icon">👥</div>
                            <div className="stat-content">
                                <span className="stat-value">15+</span>
                                <span className="stat-label">{t('about.stats.years')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;

